import { css, html, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitPage } from '../lit-page';
import '../luna-icon/luna-icon';
import '../luna-button/luna-button';

@customElement('luna-howto-overlay')
export class LunaHowtoOverlay extends LitPage {
  @property({ type: Boolean }) open = false;
  @property({ attribute: 'title' }) title = '';
  @property({ attribute: 'goal' }) goal = '';
  @property({ attribute: false }) instructions: string[] = [];

  private _focusReturn: Element | null = null;

  updated(changed: PropertyValues): void {
    if (changed.has('open')) {
      if (this.open) {
        this.activate();
      } else {
        this.deactivate();
      }
    }
  }

  private activate(): void {
    this._focusReturn = document.activeElement;
    document.body.style.overflow = 'hidden';
    this.querySelector<HTMLButtonElement>('.luna-howto-overlay__close')?.focus();
  }

  private deactivate(): void {
    document.body.style.overflow = '';
    const target = this._focusReturn;
    this._focusReturn = null;
    if (target instanceof HTMLElement) {
      target.focus();
    }
  }

  private close(): void {
    if (!this.open) {
      return;
    }
    this.open = false;
    this.dispatchEvent(
      new CustomEvent('luna-close', {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  private onScrimClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  private onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') {
      return;
    }

    const focusables = this.focusables();
    if (focusables.length === 0) {
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private focusables(): HTMLElement[] {
    return Array.from(
      this.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('disabled'));
  }

  content() {
    if (!this.open) {
      return html``;
    }

    return html`
      <div
        class="luna-howto-overlay__scrim"
        @click=${this.onScrimClick}
        @keydown=${this.onKeydown}
      >
        <div
          class="luna-howto-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="luna-howto-overlay-title"
        >
          <div class="luna-howto-overlay__titlebar">
            <luna-icon name="help" size="20" aria-hidden="true"></luna-icon>
            <span
              class="luna-howto-overlay__title mbw-type-label-button"
              id="luna-howto-overlay-title"
              >${this.title}</span
            >
            <button
              type="button"
              class="luna-howto-overlay__close"
              aria-label="Close"
              @click=${this.close}
            >
              <luna-icon name="close" size="14" aria-hidden="true"></luna-icon>
            </button>
          </div>
          <div class="luna-howto-overlay__content">
            <div class="luna-howto-overlay__body">
              <slot></slot>
              <h2 class="luna-howto-overlay__goal-title mbw-type-title-card">The goal</h2>
              <p class="luna-howto-overlay__goal mbw-type-body-default">${this.goal}</p>
              <ol class="luna-howto-overlay__steps">
                ${this.instructions.map(
                  (line, index) => html`
                    <li class="luna-howto-overlay__step">
                      <span
                        class="luna-howto-overlay__step-number mbw-type-step-number"
                        aria-hidden="true"
                        >${index + 1}</span
                      >
                      <span class="luna-howto-overlay__step-text mbw-type-body-default"
                        >${line}</span
                      >
                    </li>
                  `
                )}
              </ol>
              <luna-button variant="primary" full @click=${this.close}>
                Got it
              </luna-button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  styles() {
    return css`
      .luna-howto-overlay__scrim {
        position: fixed;
        inset: 0;
        /* Above the sticky top bar (z-index 10) in the shell. */
        z-index: 20;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--luna-surface-scrim);
      }

      .luna-howto-overlay {
        width: 560px;
        max-width: calc(100% - 32px);
        border-radius: var(--luna-radius-card-sm);
        overflow: hidden;
        background: #f4f7fb; /* pinned: figma How-to Overlay */
        box-shadow:
          0 24px 60px rgba(0, 0, 0, 0.4),
          inset 0 0 0 1px #ffffff,
          0 0 0 1px var(--luna-edge-blue); /* pinned: figma How-to Overlay */
      }

      .luna-howto-overlay__titlebar {
        display: flex;
        align-items: center;
        gap: 10px;
        height: 50px;
        padding: 0 12px 0 16px;
        color: var(--mbw-onbar-text);
        box-shadow: inset 0 1px 0 #ffffff;
      }

      .luna-howto-overlay__title {
        flex: 1;
        color: inherit;
      }

      .luna-howto-overlay__close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 28px;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        color: #ffffff;
        background-image: linear-gradient(180deg, #c94a32 0%, #b52c17 100%); /* pinned: figma How-to Overlay */
        box-shadow: inset 0 1px 0 #ffffff, 0 1px 0 var(--luna-surface-close-edge);
      }

      .luna-howto-overlay__close:focus-visible {
        outline: none;
        box-shadow:
          inset 0 1px 0 #ffffff,
          0 1px 0 var(--luna-surface-close-edge),
          0 0 0 5px #ffffff,
          0 0 0 7px var(--cds-focus);
      }

      .luna-howto-overlay__content {
        display: flex;
        flex-direction: column;
        gap: 18px;
        padding: 26px 24px;
      }

      .luna-howto-overlay__body {
        display: flex;
        flex-direction: column;
        gap: 18px;
      }

      .luna-howto-overlay__goal-title {
        margin: 0;
        color: var(--mbw-text-ink);
      }

      .luna-howto-overlay__goal {
        margin: 0;
        color: var(--mbw-text-body);
      }

      .luna-howto-overlay__steps {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 14px;
      }

      .luna-howto-overlay__step {
        display: flex;
        align-items: flex-start;
        gap: 12px;
      }

      .luna-howto-overlay__step-number {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 40px;
        width: 40px;
        height: 40px;
        border-radius: var(--luna-radius-pill);
        background: var(--luna-surface-step-circle);
        color: #ffffff;
      }

      .luna-howto-overlay__step-text {
        flex: 1;
        margin: 0;
        color: var(--mbw-text-body);
      }

      /* Titlebar reuses the top-bar gradient maps (register 6.2). */
      [data-theme='blue'] .luna-howto-overlay__titlebar {
        background-image: linear-gradient(180deg, #4a9bff 0%, #1358d8 14%, #0a46c4 60%, #0d51d6 100%); /* pinned: figma Luna Top Bar */
      }

      [data-theme='sand'] .luna-howto-overlay__titlebar {
        background-image: linear-gradient(180deg, #7a6c5e 0%, #635546 55%, #4f4339 100%); /* pinned: figma Luna Top Bar */
      }

      [data-theme='slate'] .luna-howto-overlay__titlebar {
        background-image: linear-gradient(180deg, #5a6068 0%, #41474e 16%, #2f343a 60%, #3a4046 100%); /* pinned: figma Luna Top Bar */
      }

      /* Full-screen sheet at ≤788px (BR-87): edge-to-edge, radius 0, pinned
         titlebar, scrolling content. CSS-only — no property. */
      @media (max-width: 788px) {
        .luna-howto-overlay {
          display: flex;
          flex-direction: column;
          width: 100%;
          height: 100%;
          max-width: 100%;
          border-radius: 0;
        }

        .luna-howto-overlay__titlebar {
          flex: 0 0 auto;
        }

        .luna-howto-overlay__content {
          flex: 1;
          overflow-y: auto;
        }
      }
    `;
  }
}
